import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, Edit2Icon, AlertTriangleIcon, XIcon, SaveIcon, Loader2Icon, CheckCircleIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { InventoryItem } from './types';
interface Props {
  restaurantId: string;
}
export function IngredientsTable({
  restaurantId
}: Props) {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Partial<InventoryItem> | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);
  const fetchItems = async () => {
    try {
      setLoading(true);
      const {
        data,
        error
      } = await supabase.from('inventory_items').select('*').eq('restaurant_id', restaurantId).order('ingredient_name');
      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('Error fetching ingredients:', err);
      showToast('Error al cargar ingredientes', 'error');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchItems();
  }, [restaurantId]);
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({
      message,
      type
    });
    setTimeout(() => setToast(null), 3000);
  };
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem?.ingredient_name || !editingItem?.unit) {
      showToast('Nombre y unidad son requeridos', 'error');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        ...editingItem,
        restaurant_id: restaurantId,
        cost_per_unit: Number(editingItem.cost_per_unit || 0),
        current_stock: Number(editingItem.current_stock || 0),
        minimum_stock: Number(editingItem.minimum_stock || 0),
        is_active: editingItem.is_active ?? true
      };
      const {
        error
      } = await supabase.from('inventory_items').upsert(payload);
      if (error) throw error;
      showToast('Ingrediente guardado exitosamente', 'success');
      setIsModalOpen(false);
      fetchItems();
    } catch (err) {
      console.error('Error saving ingredient:', err);
      showToast('Error al guardar ingrediente', 'error');
    } finally {
      setSaving(false);
    }
  };
  const openModal = (item?: InventoryItem) => {
    if (item) {
      setEditingItem(item);
    } else {
      setEditingItem({
        ingredient_name: '',
        unit: '',
        cost_per_unit: 0,
        current_stock: 0,
        minimum_stock: 0,
        supplier: '',
        notes: '',
        is_active: true
      });
    }
    setIsModalOpen(true);
  };
  const filteredItems = items.filter(item => item.ingredient_name.toLowerCase().includes(search.toLowerCase()));
  return <div className="space-y-4" data-id="element-200">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-201">
        <div className="relative w-full sm:w-72" data-id="element-202">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" data-id="element-203" />
          <input type="text" placeholder="Buscar ingrediente..." value={search} onChange={e => setSearch(e.target.value)} className="w-full bg-surface border border-gray-800 rounded-lg pl-9 pr-4 py-2 text-sm text-white focus:outline-none focus:border-dorado transition-colors" data-id="element-204" />
        </div>
        <button onClick={() => openModal()} className="flex items-center gap-2 bg-dorado text-carbon px-4 py-2 rounded-lg text-sm font-bold hover:bg-dorado/90 transition-colors w-full sm:w-auto justify-center" data-id="element-205">
          <PlusIcon className="w-4 h-4" data-id="element-206" />
          Nuevo Ingrediente
        </button>
      </div>

      {/* Toast Notification */}
      {toast && <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 z-50 animate-fade-in-up ${toast.type === 'success' ? 'bg-emerald-500/90 text-white' : 'bg-red-500/90 text-white'}`} data-id="element-207">
          {toast.type === 'success' ? <CheckCircleIcon className="w-5 h-5" data-id="element-208" /> : <AlertTriangleIcon className="w-5 h-5" data-id="element-209" />}
          <span className="font-medium" data-id="element-210">{toast.message}</span>
        </div>}

      {/* Table */}
      <div className="bg-surface border border-gray-800 rounded-xl overflow-hidden" data-id="element-211">
        <div className="overflow-x-auto" data-id="element-212">
          <table className="w-full text-left text-sm" data-id="element-213">
            <thead className="bg-surface-light text-gray-400 border-b border-gray-800" data-id="element-214">
              <tr data-id="element-215">
                <th className="px-4 py-3 font-medium" data-id="element-216">Ingrediente</th>
                <th className="px-4 py-3 font-medium" data-id="element-217">Unidad</th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-218">
                  Costo/Unidad
                </th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-219">
                  Stock Actual
                </th>
                <th className="px-4 py-3 font-medium text-right" data-id="element-220">
                  Stock Mínimo
                </th>
                <th className="px-4 py-3 font-medium" data-id="element-221">Estado</th>
                <th className="px-4 py-3 font-medium text-center" data-id="element-222">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800" data-id="element-223">
              {loading ? <tr data-id="element-224">
                  <td colSpan={7} className="px-4 py-8 text-center" data-id="element-225">
                    <Loader2Icon className="w-6 h-6 text-dorado animate-spin mx-auto" data-id="element-226" />
                  </td>
                </tr> : filteredItems.length === 0 ? <tr data-id="element-227">
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500" data-id="element-228">
                    No se encontraron ingredientes.
                  </td>
                </tr> : filteredItems.map(item => {
              const isOutOfStock = item.current_stock <= 0;
              const isLowStock = !isOutOfStock && item.current_stock <= item.minimum_stock;
              return <tr key={item.id} className={`hover:bg-surface-light transition-colors ${!item.is_active ? 'opacity-50' : ''}`} data-id="element-229">
                      <td className="px-4 py-3" data-id="element-230">
                        <div className="flex items-center gap-2" data-id="element-231">
                          {isOutOfStock && <div className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-232" />}
                          {isLowStock && <div className="w-1.5 h-1.5 rounded-full bg-dorado" data-id="element-233" />}
                          <span className="font-medium text-white" data-id="element-234">
                            {item.ingredient_name}
                          </span>
                        </div>
                        {item.supplier && <span className="text-xs text-gray-500 block mt-0.5" data-id="element-235">
                            {item.supplier}
                          </span>}
                      </td>
                      <td className="px-4 py-3 text-gray-400" data-id="element-236">{item.unit}</td>
                      <td className="px-4 py-3 text-right text-gray-300" data-id="element-237">
                        ₡{item.cost_per_unit.toLocaleString()}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${isOutOfStock ? 'text-red-400' : isLowStock ? 'text-dorado' : 'text-emerald-400'}`} data-id="element-238">
                        {item.current_stock}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-400" data-id="element-239">
                        {item.minimum_stock}
                      </td>
                      <td className="px-4 py-3" data-id="element-240">
                        {!item.is_active ? <span className="px-2 py-1 rounded-md text-xs font-medium bg-gray-800 text-gray-400" data-id="element-241">
                            Inactivo
                          </span> : isOutOfStock ? <span className="px-2 py-1 rounded-md text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20" data-id="element-242">
                            Sin Stock
                          </span> : isLowStock ? <span className="px-2 py-1 rounded-md text-xs font-medium bg-dorado/10 text-dorado border border-dorado/20" data-id="element-243">
                            Stock Bajo
                          </span> : <span className="px-2 py-1 rounded-md text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" data-id="element-244">
                            OK
                          </span>}
                      </td>
                      <td className="px-4 py-3 text-center" data-id="element-245">
                        <button onClick={() => openModal(item)} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors" title="Editar" data-id="element-246">
                          <Edit2Icon className="w-4 h-4" data-id="element-247" />
                        </button>
                      </td>
                    </tr>;
            })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && editingItem && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" data-id="element-248">
          <div className="bg-surface border border-gray-800 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-249">
            <div className="flex items-center justify-between p-4 border-b border-gray-800" data-id="element-250">
              <h3 className="text-lg font-heading font-bold text-white" data-id="element-251">
                {editingItem.id ? 'Editar Ingrediente' : 'Nuevo Ingrediente'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white" data-id="element-252">
                <XIcon className="w-5 h-5" data-id="element-253" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1 custom-scrollbar" data-id="element-254">
              <form id="ingredient-form" onSubmit={handleSave} className="space-y-4" data-id="element-255">
                <div data-id="element-256">
                  <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-257">
                    Nombre del Ingrediente *
                  </label>
                  <input type="text" required value={editingItem.ingredient_name || ''} onChange={e => setEditingItem({
                ...editingItem,
                ingredient_name: e.target.value
              })} className="w-full bg-carbon border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-dorado focus:outline-none" data-id="element-258" />
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-259">
                  <div data-id="element-260">
                    <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-261">
                      Unidad de Medida *
                    </label>
                    <input type="text" required placeholder="ej. kg, g, ml, l, unidad" value={editingItem.unit || ''} onChange={e => setEditingItem({
                  ...editingItem,
                  unit: e.target.value
                })} className="w-full bg-carbon border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-dorado focus:outline-none" data-id="element-262" />
                  </div>
                  <div data-id="element-263">
                    <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-264">
                      Costo por Unidad (₡) *
                    </label>
                    <input type="number" required min="0" step="0.01" value={editingItem.cost_per_unit || ''} onChange={e => setEditingItem({
                  ...editingItem,
                  cost_per_unit: parseFloat(e.target.value)
                })} className="w-full bg-carbon border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-dorado focus:outline-none" data-id="element-265" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4" data-id="element-266">
                  <div data-id="element-267">
                    <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-268">
                      Stock Actual *
                    </label>
                    <input type="number" required step="0.01" value={editingItem.current_stock || ''} onChange={e => setEditingItem({
                  ...editingItem,
                  current_stock: parseFloat(e.target.value)
                })} className="w-full bg-carbon border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-dorado focus:outline-none" data-id="element-269" />
                  </div>
                  <div data-id="element-270">
                    <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-271">
                      Stock Mínimo (Alerta) *
                    </label>
                    <input type="number" required min="0" step="0.01" value={editingItem.minimum_stock || ''} onChange={e => setEditingItem({
                  ...editingItem,
                  minimum_stock: parseFloat(e.target.value)
                })} className="w-full bg-carbon border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-dorado focus:outline-none" data-id="element-272" />
                  </div>
                </div>

                <div data-id="element-273">
                  <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-274">
                    Proveedor (Opcional)
                  </label>
                  <input type="text" value={editingItem.supplier || ''} onChange={e => setEditingItem({
                ...editingItem,
                supplier: e.target.value
              })} className="w-full bg-carbon border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-dorado focus:outline-none" data-id="element-275" />
                </div>

                <div data-id="element-276">
                  <label className="block text-xs font-medium text-gray-400 mb-1" data-id="element-277">
                    Notas (Opcional)
                  </label>
                  <textarea rows={2} value={editingItem.notes || ''} onChange={e => setEditingItem({
                ...editingItem,
                notes: e.target.value
              })} className="w-full bg-carbon border border-gray-700 rounded-lg px-3 py-2 text-white focus:border-dorado focus:outline-none resize-none" data-id="element-278" />
                </div>

                <div className="flex items-center gap-2 pt-2" data-id="element-279">
                  <input type="checkbox" id="is_active" checked={editingItem.is_active} onChange={e => setEditingItem({
                ...editingItem,
                is_active: e.target.checked
              })} className="w-4 h-4 rounded border-gray-700 text-dorado focus:ring-dorado bg-carbon" data-id="element-280" />
                  <label htmlFor="is_active" className="text-sm text-white cursor-pointer" data-id="element-281">
                    Ingrediente Activo
                  </label>
                </div>
              </form>
            </div>

            <div className="p-4 border-t border-gray-800 flex justify-end gap-3 bg-surface-light" data-id="element-282">
              <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 rounded-lg text-sm font-medium text-gray-400 hover:text-white hover:bg-gray-800 transition-colors" data-id="element-283">
                Cancelar
              </button>
              <button type="submit" form="ingredient-form" disabled={saving} className="flex items-center gap-2 bg-dorado text-carbon px-4 py-2 rounded-lg text-sm font-bold hover:bg-dorado/90 transition-colors disabled:opacity-50" data-id="element-284">
                {saving ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-285" /> : <SaveIcon className="w-4 h-4" data-id="element-286" />}
                Guardar
              </button>
            </div>
          </div>
        </div>}
    </div>;
}